import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

// ─── Inline style tokens ────────────────────────────────────────────────────

const COLOR = {
  primary:   '#2563eb',
  danger:    '#dc2626',
  muted:     '#9ca3af',
  border:    '#e5e7eb',
  bg:        '#f9fafb',
  text:      '#111827',
  secondary: '#6b7280',
  green:     { background: '#d1fae5', color: '#065f46' },
  blue:      { background: '#dbeafe', color: '#1e40af' },
  orange:    { background: '#ffedd5', color: '#9a3412' },
  gray:      { background: '#f3f4f6', color: '#374151' },
  purple:    { background: '#ede9fe', color: '#5b21b6' },
};

const S = {
  pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 },
  h1: { fontSize: 20, fontWeight: 700, margin: 0 },
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: '14px 16px', marginBottom: 10 },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  userInfo: { display: 'flex', alignItems: 'center', gap: 12 },
  actions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' },
  userName: { fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 },
  userEmail: { fontSize: 13, color: COLOR.secondary },
  sectionTitle: { fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: COLOR.secondary, margin: '16px 0 8px' },
  infoBox: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1e40af', marginBottom: 16, display: 'flex', gap: 8 },
  legendGrid: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  legendCard: { flex: '1 1 150px', background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: '12px 14px' },
  legendDesc: { fontSize: 12, color: COLOR.secondary, marginTop: 6 },
  empty: { textAlign: 'center', color: COLOR.secondary, padding: 32 },
  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, padding: '24px 20px 32px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', margin: '0 16px' },
  modalHandle: { display: 'none' },
  modalTitle: { fontWeight: 700, fontSize: 17, marginBottom: 16 },
  formGroup: { marginBottom: 14 },
  formLabel: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: COLOR.text },
  formInput: { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${COLOR.border}`, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  formSelect: { width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${COLOR.border}`, fontSize: 14, boxSizing: 'border-box', background: '#fff' },
  btnRow: { display: 'flex', gap: 8, marginTop: 16 },
  systemRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  systemLabel: { fontWeight: 600, fontSize: 14 },
  systemDesc: { fontSize: 12, color: COLOR.secondary },
};

const BTN = {
  base: { borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: '7px 14px', border: 'none', flex: 1 },
  primary: { background: COLOR.primary, color: '#fff' },
  outline: { background: 'transparent', color: COLOR.text, border: `1px solid ${COLOR.border}` },
  danger:  { background: 'transparent', color: COLOR.danger, border: `1px solid ${COLOR.danger}` },
  sm:      { padding: '5px 10px', fontSize: 12, flex: 'unset' },
  dangerFill: { background: COLOR.danger, color: '#fff' },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function Avatar({ name, active }) {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      background: active ? COLOR.primary : COLOR.muted,
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 16, flexShrink: 0,
    }}>
      {name?.charAt(0)?.toUpperCase() ?? '?'}
    </div>
  );
}

function Badge({ color = 'gray', children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
      ...COLOR[color],
    }}>
      {children}
    </span>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" onClick={onChange} disabled={disabled} style={{
      width: 44, height: 24, borderRadius: 12, border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: checked ? COLOR.primary : COLOR.border,
      position: 'relative', padding: 0, flexShrink: 0,
      opacity: disabled ? .6 : 1,
    }}>
      <span style={{
        position: 'absolute', top: 2,
        left: checked ? 22 : 2,
        width: 20, height: 20, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        transition: 'left .15s',
      }} />
    </button>
  );
}

function Btn({ variant = 'primary', sm, onClick, disabled, type = 'button', children, style }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...BTN.base,
        ...BTN[variant],
        ...(sm ? BTN.sm : {}),
        ...(disabled ? { opacity: .6, cursor: 'not-allowed' } : {}),
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: `3px solid ${COLOR.border}`,
        borderTopColor: COLOR.primary,
        animation: 'sf-spin .7s linear infinite',
      }} />
      <style>{`@keyframes sf-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── SystemToggleRow ─────────────────────────────────────────────────────────

function SystemToggleRow({ label, description, active, disabled, loading, onChange, onEdit, warn }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
      <div style={{ flex: 1, textAlign: 'right' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: disabled ? COLOR.muted : COLOR.text }}>{label}</span>
        {description && <div style={{ fontSize: 11, color: COLOR.secondary }}>{description}</div>}
      </div>
      <Toggle checked={active && !disabled} onChange={disabled ? undefined : onChange} disabled={loading || disabled} />
      {onEdit && (
        <button type="button" onClick={onEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLOR.secondary, fontSize: 14, padding: 0 }} title="Editar papel">✏️</button>
      )}
    </div>
  );
}

// ─── Role config ─────────────────────────────────────────────────────────────

const ROLE_LABEL = { driver: 'Motorista', manager: 'Gerente', admin: 'Administrador' };
const ROLE_COLOR = { driver: 'blue', manager: 'orange', admin: 'green' };
const ROLES = ['driver', 'manager', 'admin'];

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * UserManagement — gerenciador de usuários e acessos por sistema.
 */
export default function UserManagement({ usersApi, userRolesApi = null, t = (key) => key }) {
  const hasFleet = !!userRolesApi;

  const [users, setUsers]     = useState([]);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [togglingKey, setTogglingKey] = useState(null); // 'active-{id}' | 'financial-{id}' | 'fleet-{id}'

  // modal: null | { mode: 'create' | 'edit', user? }
  const [modal, setModal] = useState(null);
  const [newForm, setNewForm] = useState({ name: '', email: '', document_number: '', financial: false, frotas: false, role: 'driver' });
  const [editForm, setEditForm] = useState({ fleetActive: false, role: 'driver' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [usersData, rolesData] = await Promise.all([
        usersApi.list(),
        hasFleet ? userRolesApi.list() : Promise.resolve([]),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fleetRoleOf = (userId) => roles.find((r) => r.user_id === userId) ?? null;

  // ── Create ──────────────────────────────────────────────────────────────────
  const maskCpf = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    return d.replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const openCreate = () => {
    setNewForm({ name: '', email: '', document_number: '', financial: false, frotas: false, role: 'driver' });
    setModal({ mode: 'create' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { user } = await usersApi.create({ name: newForm.name, email: newForm.email, document_number: newForm.document_number });
      if (newForm.financial) {
        await usersApi.toggleFinancialActive(user.id, true);
      }
      if (hasFleet && newForm.frotas) {
        await userRolesApi.create({ user_id: user.id, role: newForm.role });
      }
      setModal(null);
      await load();
      Swal.fire({ icon: 'success', title: t('Usuário criado!'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('Erro'), text: err.response?.data?.message || t('Erro ao criar usuário') });
    } finally {
      setSaving(false);
    }
  };

  // ── Edit access ─────────────────────────────────────────────────────────────
  const openEdit = (user) => {
    const entry = fleetRoleOf(user.id);
    setEditForm({ fleetActive: entry ? (entry.is_active ?? true) : false, role: entry?.role ?? 'driver' });
    setModal({ mode: 'edit', user, entry });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { user, entry } = modal;
    try {
      if (hasFleet) {
        if (editForm.fleetActive && !entry) {
          await userRolesApi.create({ user_id: user.id, role: editForm.role, is_active: true });
        } else if (entry) {
          await userRolesApi.update(entry.id, { role: editForm.role, is_active: editForm.fleetActive });
        }
      }
      setModal(null);
      await load();
      Swal.fire({ icon: 'success', title: t('Acesso atualizado!'), timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('Erro'), text: err.response?.data?.message || t('Erro ao salvar') });
    } finally {
      setSaving(false);
    }
  };

  // ── Toggles ─────────────────────────────────────────────────────────────────
  const withToggle = (key, fn) => async () => {
    setTogglingKey(key);
    try { await fn(); await load(); }
    catch (err) { Swal.fire({ icon: 'error', title: t('Erro'), text: err.response?.data?.message || t('Erro') }); }
    finally { setTogglingKey(null); }
  };

  const toggleActive          = (user) => withToggle(`active-${user.id}`,
    () => usersApi.toggleActive(user.id, !user.is_active))();

  const toggleFinancialActive = (user) => withToggle(`financial-${user.id}`,
    () => usersApi.toggleFinancialActive(user.id, !user.financial_active))();

  const toggleFleetActive     = (entry) => withToggle(`fleet-${entry.id}`,
    () => userRolesApi.update(entry.id, { is_active: !entry.is_active }))();

  // ─────────────────────────────────────────────────────────────────────────────

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={S.pageHeader}>
        <h1 style={S.h1}>{t('Usuários e Acessos')}</h1>
        <Btn onClick={openCreate} sm>{'+ ' + t('Novo usuário')}</Btn>
      </div>

      <div style={S.infoBox}>
        <span>ℹ️</span>
        <span>{t('Gerencie os usuários da empresa e os sistemas que cada um pode acessar.')}</span>
      </div>

      {/* Legend */}
      <div style={S.legendGrid}>
        <div style={S.legendCard}>
          <Badge color="green">{'💼 ' + t('Sistema Financeiro')}</Badge>
          <p style={S.legendDesc}>{t('Receitas, despesas, bancos, cartões e relatórios. Controlado pelo status ativo/inativo.')}</p>
        </div>
        {hasFleet && (
          <div style={S.legendCard}>
            <Badge color="blue">{'🚛 ' + t('Gestão de Frotas')}</Badge>
            <p style={S.legendDesc}>{t('Viagens, veículos, motoristas e manutenção. Requer papel: Motorista, Gerente ou Administrador.')}</p>
          </div>
        )}
      </div>

      <div style={S.sectionTitle}>{t('Usuários da empresa')} ({users.length})</div>

      {users.length === 0 ? (
        <div style={{ ...S.card, ...S.empty }}>{t('Nenhum usuário cadastrado.')}</div>
      ) : (
        users.map((user) => {
          const entry = fleetRoleOf(user.id);
          return (
            <div key={user.id} style={S.card}>
              <div style={S.row}>
                <div style={S.userInfo}>
                  <Avatar name={user.name} active={user.is_active} />
                  <div>
                    <div style={S.userName}>
                      {user.name}
                      {user.is_super_admin && <Badge color="purple">{t('Super Admin')}</Badge>}
                      {!user.is_active && <Badge color="gray">{t('Inativo')}</Badge>}
                    </div>
                    <div style={S.userEmail}>{user.email}</div>
                  </div>
                </div>

                {!user.is_super_admin ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                    {/* Conta global */}
                    <SystemToggleRow
                      label={t('Conta')}
                      description={t('Login em todos os sistemas')}
                      active={user.is_active}
                      loading={togglingKey === `active-${user.id}`}
                      onChange={() => toggleActive(user)}
                      warn={!user.is_active ? null : t('Desativa login em todos os sistemas')}
                    />
                    {/* Financeiro */}
                    <SystemToggleRow
                      label={'💼 ' + t('Financeiro')}
                      active={user.is_active && user.financial_active}
                      disabled={!user.is_active}
                      loading={togglingKey === `financial-${user.id}`}
                      onChange={() => toggleFinancialActive(user)}
                    />
                    {/* Frotas */}
                    {hasFleet && (
                      entry ? (
                        <SystemToggleRow
                          label={`🚛 ${t('Frotas')} · ${t(ROLE_LABEL[entry.role])}`}
                          active={user.is_active && entry.is_active}
                          disabled={!user.is_active}
                          loading={togglingKey === `fleet-${entry.id}`}
                          onChange={() => toggleFleetActive(entry)}
                          onEdit={() => openEdit(user)}
                        />
                      ) : (
                        <SystemToggleRow
                          label={`🚛 ${t('Frotas')}`}
                          active={false}
                          disabled={!user.is_active}
                          loading={false}
                          onChange={() => openEdit(user)}
                        />
                      )
                    )}
                  </div>
                ) : (
                  <Badge color="purple">{t('Super Admin — acesso total')}</Badge>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* ── Modal: Novo usuário ─────────────────────────────────────────────── */}
      {modal?.mode === 'create' && (
        <div style={S.overlay} onClick={(e) => void 0}>
          <div style={S.modal}>
            <div style={S.modalHandle} />
            <div style={S.modalTitle}>{t('Novo usuário')}</div>
            <form onSubmit={handleCreate}>
              <div style={S.formGroup}>
                <label style={S.formLabel}>{t('Nome *')}</label>
                <input style={S.formInput} type="text" required
                  value={newForm.name}
                  onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>{t('E-mail *')}</label>
                <input style={S.formInput} type="email" required
                  value={newForm.email}
                  onChange={(e) => setNewForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.formLabel}>{t('CPF *')}</label>
                <input style={S.formInput} type="text" required placeholder="000.000.000-00"
                  value={newForm.document_number}
                  onChange={(e) => setNewForm((f) => ({ ...f, document_number: maskCpf(e.target.value) }))} />
              </div>

              <div style={S.sectionTitle}>{t('Sistemas')}</div>

              <div style={S.systemRow}>
                <div>
                  <div style={S.systemLabel}>{'💼 ' + t('Sistema Financeiro')}</div>
                  <div style={S.systemDesc}>{t('Receitas, despesas, relatórios')}</div>
                </div>
                <Toggle
                  checked={newForm.financial}
                  onChange={() => setNewForm((f) => ({ ...f, financial: !f.financial }))}
                />
              </div>

              {hasFleet && (
                <>
                  <div style={S.systemRow}>
                    <div>
                      <div style={S.systemLabel}>{'🚛 ' + t('Gestão de Frotas')}</div>
                      <div style={S.systemDesc}>{t('Requer papel de acesso')}</div>
                    </div>
                    <Toggle
                      checked={newForm.frotas}
                      onChange={() => setNewForm((f) => ({ ...f, frotas: !f.frotas }))}
                    />
                  </div>
                  {newForm.frotas && (
                    <div style={S.formGroup}>
                      <label style={S.formLabel}>{t('Papel na Gestão de Frotas')}</label>
                      <select style={S.formSelect} value={newForm.role}
                        onChange={(e) => setNewForm((f) => ({ ...f, role: e.target.value }))}>
                        {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div style={S.btnRow}>
                <Btn variant="outline" onClick={() => setModal(null)}>{t('Cancelar')}</Btn>
                <Btn type="submit" disabled={saving}>{saving ? t('Criando...') : t('Criar')}</Btn>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Editar acessos ───────────────────────────────────────────── */}
      {modal?.mode === 'edit' && (
        <div style={S.overlay} onClick={(e) => void 0}>
          <div style={S.modal}>
            <div style={S.modalHandle} />
            <div style={S.modalTitle}>{t('Acessos')} — {modal.user.name}</div>
            <form onSubmit={handleEdit}>
              <div style={S.sectionTitle}>{t('Papel na Gestão de Frotas')}</div>

              <div style={{ ...S.systemRow, marginBottom: 8 }}>
                <div>
                  <div style={S.systemLabel}>{'🚛 ' + t('Acesso às Frotas')}</div>
                  <div style={S.systemDesc}>{t('Ativa ou suspende o acesso sem remover o papel')}</div>
                </div>
                <Toggle
                  checked={editForm.fleetActive}
                  onChange={() => setEditForm((f) => ({ ...f, fleetActive: !f.fleetActive }))}
                />
              </div>

              {(editForm.fleetActive || modal.entry) && (
                <div style={S.formGroup}>
                  <label style={S.formLabel}>{t('Papel')}</label>
                  <select style={S.formSelect} value={editForm.role}
                    onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}>
                    {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
                  </select>
                </div>
              )}

              <div style={S.btnRow}>
                <Btn variant="outline" onClick={() => setModal(null)}>{t('Cancelar')}</Btn>
                <Btn type="submit" disabled={saving}>{saving ? t('Salvando...') : t('Salvar')}</Btn>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
